import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import {
  ClipboardCheck, Search, ArrowDown, ArrowUp, FileDown, AlertCircle,
  Timer, TrendingUp, CheckCircle2,
} from "lucide-react";

const ACCENT = "#A78BFA";  // FINISH dept accent

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtNum1(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}
function fmtNum2(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" });
  } catch { return iso; }
}
function gapColor(gap, threshold = 3) {
  if (gap === null || gap === undefined) return "text-muted-foreground";
  if (gap <= threshold) return "text-emerald-400";
  if (gap <= threshold * 2) return "text-amber-400";
  return "text-red-400";
}

const STATUS_BADGE = {
  PASS:      "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  UNCHECKED: "bg-amber-500/10 border-amber-500/30 text-amber-400",
};

export default function PatchJobPerformanceCard({ showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [comment, setComment] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("gap");
  const [sort, setSort] = useState("desc");
  const [data, setData] = useState({ rows: [], categories: [], statuses: [], comments: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico, sort_by: sortBy, sort };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (status)   p.status   = status;
    if (category) p.category = category;
    if (comment)  p.comment  = comment;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [filter, showMexico, sortBy, sort, status, category, comment, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/FINISH/patch-job-performance`, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const toggleSort = (key) => {
    if (sortBy === key) setSort((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortBy(key); setSort(key === "cast" ? "asc" : "desc"); }
  };

  const handleExport = (fmt) => {
    const qs = new URLSearchParams({ fmt, preset: filter.preset, show_mexico: String(showMexico), sort_by: sortBy, sort });
    if (filter.preset === "custom") { qs.set("start", filter.start); qs.set("end", filter.end); }
    if (status)   qs.set("status", status);
    if (category) qs.set("category", category);
    if (search.trim()) qs.set("search", search.trim());
    // Backend export endpoint isn't wired yet — clientside CSV as a graceful fallback.
    const header = ["Job","Job Name","Cast Date","Checklist Date","Category","Status",
                    "Piece Mark","Piece S/N","Comments","Total SF","Total Hours","Detection Gap Days"];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    (data.rows || []).forEach((r) => {
      lines.push([
        r.job, r.job_name, r.cast_date, r.check_list_date, r.category, r.status,
        r.piece_mark, r.piece_sn, r.comments, r.total_sf, r.total_hours, r.detection_gap_days,
      ].map(escape).join(","));
    });
    if (fmt === "csv") {
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `patch-job-performance-${showMexico ? "mexico" : "slc"}-${filter.preset}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const summary = data.summary || {};

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden"
      data-testid="patch-job-performance-card"
    >
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, #FBBF24 100%)` }}
      />

      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl">Patch Job Performance</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              · piece-level QC · {summary.total ?? 0} pieces
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="patch-perf-csv"
              onClick={() => handleExport("csv")}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-[#0A0B10] hover:border-[#A78BFA]/60 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white transition-colors"
            >
              <FileDown size={11} /> CSV
            </button>
          </div>
        </div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-px bg-border" data-testid="patch-perf-summary">
        <Tile label="Total Pieces" value={fmtNum(summary.total)} accent={ACCENT} testId="patch-tile-total" />
        <Tile label="Passed" value={fmtNum(summary.passed)} accent="#34D399" testId="patch-tile-passed" icon={<CheckCircle2 size={12} />} />
        <Tile label="Unchecked" value={fmtNum(summary.unchecked)} accent="#FBBF24" testId="patch-tile-unchecked" icon={<AlertCircle size={12} />} />
        <Tile label="Pass Rate" value={summary.pass_rate !== undefined ? `${(summary.pass_rate || 0).toFixed(1)}%` : "—"} accent="#10B981" testId="patch-tile-pass-rate" />
        <Tile label="Total SF" value={fmtNum1(summary.total_sf)} accent="#00D4FF" testId="patch-tile-sf" />
        <Tile label="Avg Gap" value={fmtNum1(summary.avg_gap_days)} accent="#00D4FF" testId="patch-tile-avg-gap" icon={<TrendingUp size={12} />} sub="days" />
        <Tile label="Max Gap" value={fmtNum(summary.max_gap_days)} accent="#F87171" testId="patch-tile-max-gap" icon={<Timer size={12} />} sub="days" />
      </div>

      {/* Filters bar */}
      <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="patch-perf-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job / piece / comment…"
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA]"
          />
        </div>
        <select
          data-testid="patch-perf-status"
          value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA]"
        >
          <option value="">All Status</option>
          {(data.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="patch-perf-category"
          value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA] max-w-xs"
        >
          <option value="">All Categories</option>
          {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="patch-perf-comment"
          value={comment} onChange={(e) => setComment(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA] max-w-xs min-w-[180px]"
          title="Filter rows by exact Comments value"
        >
          <option value="">All Comments</option>
          {(data.comments || []).map((c) => (
            <option key={c} value={c}>
              {c === "(none)" ? "(no comment)" : (c.length > 50 ? c.slice(0, 47) + "…" : c)}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[560px]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8] sticky top-0 z-10">
            <tr>
              <th className="py-2.5 px-3">Job</th>
              <th className="py-2.5 px-3">Job Name</th>
              <th className="py-2.5 px-3">
                <SortHeader label="Cast Date" sortKey="cast" active={sortBy} dir={sort} onClick={toggleSort} testid="patch-th-cast" />
              </th>
              <th className="py-2.5 px-3">Checklist Date</th>
              <th className="py-2.5 px-3">Category</th>
              <th className="py-2.5 px-3">Status</th>
              <th className="py-2.5 px-3">Piece Mark</th>
              <th className="py-2.5 px-3">Piece S/N</th>
              <th className="py-2.5 px-3">Comments</th>
              <th className="py-2.5 px-3 text-right">
                <SortHeader label="Total SF" sortKey="sf" active={sortBy} dir={sort} onClick={toggleSort} align="right" testid="patch-th-sf" />
              </th>
              <th className="py-2.5 px-3 text-right">
                <SortHeader label="Total Hours" sortKey="hours" active={sortBy} dir={sort} onClick={toggleSort} align="right" testid="patch-th-hours" />
              </th>
              <th className="py-2.5 px-3 text-right">
                <SortHeader label="Detection Gap Days" sortKey="gap" active={sortBy} dir={sort} onClick={toggleSort} align="right" testid="patch-th-gap" />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={12} className="py-12 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && (data.rows || []).length === 0 && (
              <tr><td colSpan={12} className="py-12 text-center uppercase tracking-widest text-muted-foreground">No pieces match the filters</td></tr>
            )}
            {!loading && (data.rows || []).map((r, i) => (
              <tr key={`${r.job}-${r.piece_sn}-${i}`} className="border-t border-border/40 hover:bg-[#1A1C28]/40" data-testid={`patch-row-${i}`}>
                <td className="py-2 px-3 font-mono" style={{ color: ACCENT }}>{r.job || "—"}</td>
                <td className="py-2 px-3 max-w-[260px] truncate" title={r.job_name}>{r.job_name || "—"}</td>
                <td className="py-2 px-3 font-mono">{fmtDate(r.cast_date)}</td>
                <td className="py-2 px-3 font-mono">{fmtDate(r.check_list_date)}</td>
                <td className="py-2 px-3">{r.category || "—"}</td>
                <td className="py-2 px-3">
                  <span className={`inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${STATUS_BADGE[(r.status || "").toUpperCase()] || "border-border text-muted-foreground"}`}>
                    {r.status || "—"}
                  </span>
                </td>
                <td className="py-2 px-3 font-mono">{r.piece_mark || "—"}</td>
                <td className="py-2 px-3 font-mono text-muted-foreground">{r.piece_sn || "—"}</td>
                <td className="py-2 px-3 max-w-[240px] truncate text-muted-foreground" title={r.comments}>{r.comments || "—"}</td>
                <td className="py-2 px-3 font-mono text-right tabular-nums">{fmtNum1(r.total_sf)}</td>
                <td className="py-2 px-3 font-mono text-right tabular-nums">{fmtNum2(r.total_hours)}</td>
                <td className={`py-2 px-3 font-mono text-right tabular-nums font-semibold ${gapColor(r.detection_gap_days)}`}
                    data-testid={`patch-row-${i}-gap`}>
                  {r.detection_gap_days === null || r.detection_gap_days === undefined ? "—" : fmtNum(r.detection_gap_days)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-2.5 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
        <span>Showing {(data.rows || []).length} pieces</span>
        <span>Total SF: {fmtNum1(summary.total_sf)} · Total Hours: {fmtNum2(summary.total_hours)} · Avg Gap: {fmtNum1(summary.avg_gap_days)}d</span>
      </div>
    </section>
  );
}

function Tile({ label, value, accent, sub, testId, icon }) {
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        {icon && <span style={{ color: accent }}>{icon}</span>}
        {label}
      </div>
      <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function SortHeader({ label, sortKey, active, dir, onClick, align, testid, accent = "#A78BFA" }) {
  const isActive = active === sortKey;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onClick(sortKey)}
      className={`inline-flex items-center gap-1 uppercase tracking-widest text-[10px] transition-colors ${
        isActive ? "text-white" : "text-[#94A3B8] hover:text-white"
      } ${align === "right" ? "justify-end w-full" : ""}`}
    >
      <span>{label}</span>
      {isActive ? (
        dir === "desc"
          ? <ArrowDown size={11} style={{ color: accent }} />
          : <ArrowUp size={11} style={{ color: accent }} />
      ) : (
        <ArrowDown size={11} className="opacity-30" />
      )}
    </button>
  );
}
